import { useEffect } from 'react'
import { Navigate, Outlet, useLocation, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { SUPPORTED_LOCALES } from '../../i18n'
import { setApiLocale } from '../../lib/api/client'
import { ErrorBoundary } from '../ErrorBoundary'
import { MobileFooterNav } from './MobileFooterNav'
import type { Locale } from '../../lib/api/types'

// These steps use StepShell (own bottom action bar) and handle their own bottom spacing — incl.
// clearing the always-visible tab nav. Every other page gets the nav padding here.
const STEP_SHELL_PAGES = new Set(['book', 'rate', 'info', 'payment'])

/** Validates the :locale segment and syncs it to i18n + the API client. */
export function LocaleLayout() {
  const { locale } = useParams()
  const { i18n } = useTranslation()
  const location = useLocation()
  const valid = locale && SUPPORTED_LOCALES.includes(locale as Locale)

  useEffect(() => {
    if (valid) {
      const l = locale as Locale
      if (i18n.language !== l) i18n.changeLanguage(l)
      setApiLocale(l)
      window.localStorage.setItem('gsm_locale', l)
      document.documentElement.lang = l
    }
  }, [locale, valid, i18n])

  // Every navigation starts a fresh page at the TOP — don't keep the previous page's scroll.
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [location.pathname])

  if (!valid) return <Navigate to={`/${i18n.language || 'it'}`} replace />

  // The bottom tab nav is ALWAYS visible (including the booking funnel). StepShell pages handle
  // their own bottom spacing; every other page gets padding here so content clears the nav.
  const page = location.pathname.split('/').filter(Boolean)[1] ?? ''
  const usesStepShell = STEP_SHELL_PAGES.has(page)

  // Per-route guard: a crash on one screen shows the fallback and auto-recovers on navigation.
  return (
    <ErrorBoundary resetKey={location.pathname}>
      {usesStepShell ? (
        <Outlet />
      ) : (
        <div className="pb-[calc(60px_+_env(safe-area-inset-bottom))] lg:pb-0">
          <Outlet />
        </div>
      )}
      <MobileFooterNav />
    </ErrorBoundary>
  )
}
